import React, { useMemo, useState, useReducer, useEffect } from 'react';
import { Icon, Divider, Header, Loader, Button } from 'semantic-ui-react';
import InputMask from 'react-input-mask';
import { DateTime } from 'luxon';
import { toast } from 'react-toastify';
import produce from 'immer';

import PageHeader from 'components/PageHeader';
import request from 'services/http';
import { tap, finalize } from 'rxjs/operators';
import localDb from '../localStorage';

import './Afericao.scss';

const initialState = {
    measures: new Map(),
};

function measureReducer(state, action) {
    switch (action.type) {
        case 'reset':
            return { measures: new Map() };

        case 'payload':
            return state;

        default:
            const { id, ...data } = action.payload.measure;
            const measures = state.measures;

            const hasMeasure = measures.get(id);
            const now = DateTime.local().toFormat('yyyy-MM-dd hh:mm:ss');
            if (hasMeasure) {
                measures.set(id, { ...measures.get(id), ...data, updated_at: now });
            } else {
                measures.set(id, {
                    ...measures.get(id),
                    ...data,
                    updated_at: now,
                    started_at: now,
                });
            }

            return { ...state, measures };
    }
}

export default function Afericao() {
    const [bicos, setBicos] = useState([]);
    const [visualData, setVisualData] = useState({
        loading: true,
        cantFinalize: true,
        finalizing: false,
        counter: 0,
        canErase: false,
    });
    const [measurePhotos, setMeasurePhotos] = useState(new FormData());

    const [measures, setMeasures] = useReducer(measureReducer, initialState);

    useMemo(() => {
        const req = request({
            method: 'GET',
            url: '/combustivel/bico/',
        })
            .pipe(
                tap(({ response }) => {
                    setBicos(response);
                    getStoredData(response);
                }),
                finalize(() => {
                    return req.unsubscribe();
                }),
            )
            .subscribe();

        const getStoredData = async (bicos) => {
            const updatedBeaks = [];
            for (const beak of bicos) {
                const storedData = await localDb.selectOne(beak.serie);
                updatedBeaks.push({ ...beak, ...storedData });
                if (storedData) {
                    setMeasures({ payload: { measure: { id: beak.serie, ...storedData } } });
                }
            }
            setVisualData(
                produce((draft) => {
                    draft.loading = false;
                }),
            );
        };
    }, []);

    useEffect(() => {
        localDb.willSave();
    }, []);

    function handleInput(id) {
        const e = window.event;
        const { value: tempValue, name } = e.target;

        const initial = tempValue.length === 1;
        const isSignal = /\+|-/.test(tempValue);
        const isAfericao = name === 'rapida' || name === 'lenta';
        const isZero = tempValue === '0';

        if (initial && !isSignal && isAfericao && !isZero) {
            e.target.value = '+' + e.target.value;
        }

        const { value } = e.target;
        return setMeasures({ payload: { measure: { id, [name]: value } } });
    }

    async function saveOnBlur(id, bico = 0) {
        const e = window.event;
        const { value, name } = e.target;

        if (name === 'encerranteMecFim') {
            canFinish({});
        }

        if (name === 'encerranteEletFim' && value !== '') {
            const {
                encerranteMecIni: mecIni,
                encerranteMecFim: mecFim,
                encerranteEletIni: eletIni,
                encerranteEletFim: eletFim,
            } = measures.measures.get(id);

            const mec = parseInt(mecFim.split('.').join('')) - parseInt(mecIni.split('.').join(''));
            const elet =
                parseInt(eletFim.split('.').slice(0, 3).join('')) -
                parseInt(eletIni.split('.').slice(0, 3).join(''));

            if (mec !== elet) {
                toast.warn(`Os encerrantes do bico ${bico} podem estar incorretos`);
            }
        }

        localDb.insert({
            id,
            [name]: value,
        });
    }

    function onKeyDownMeasure(e) {
        const val = String.fromCharCode(e.keyCode);

        if (/[A-Z]/.test(val)) {
            e.preventDefault();
        }
    }

    function handleInputFiles() {
        const { files } = window.event.target;
        const acceptFiles = ['image/png', 'image/jpg', 'image/jpeg', 'application/pdf'];

        if (!files) {
            return;
        }

        const photos = measurePhotos;

        for (const file of files) {
            if (acceptFiles.includes(file.type)) {
                photos.append('photos', file);
            }
        }

        const counter = photos.getAll('photos').length;
        setMeasurePhotos(photos);

        canFinish({ erase: true, counter });
    }

    const canFinish = ({ erase, counter }) => {
        if (visualData.finalizing) {
            return;
        }

        const items = Array.from(measures.measures.keys()).filter(
            (id) => measures.measures.get(id).rapida,
        );

        if (items.length <= measurePhotos.getAll('photos').length && items.length > 0) {
            if (erase !== undefined) {
                return setVisualData({
                    ...visualData,
                    cantFinalize: false,
                    canErase: erase,
                    counter,
                });
            }
            return setVisualData({ ...visualData, cantFinalize: false });
        }

        if (erase !== undefined) {
            return setVisualData({
                ...visualData,
                cantFinalize: true,
                canErase: erase,
                counter,
            });
        }
        setVisualData({ ...visualData, cantFinalize: true });
    };

    const sendData = () => {
        if (measurePhotos.getAll('photos')?.length === 0) {
            return;
        }

        const data = measurePhotos;
        Array.from(measures.measures.keys()).forEach((id) => {
            if (!measures.measures.get(id).rapida) {
                return;
            }
            data.append(
                'inputs',
                JSON.stringify({
                    id,
                    ...measures.measures.get(id),
                }),
            );
        });

        if (measurePhotos.getAll('inputs').length < 0) {
            return;
        }
        setVisualData({ ...visualData, cantFinalize: true, finalizing: true });

        request({
            url: '/combustivel/afericao/',
            method: 'POST',
            body: data,
        })
            .toPromise()
            .catch((err) => {
                setVisualData({ ...visualData, cantFinalize: false });
                measurePhotos.delete('inputs');
                toast.error('Falha ao tentar salvar os dados!');
            })
            .then((data) => {
                if (data) {
                    measurePhotos.delete('photos');
                    measurePhotos.delete('inputs');
                    toast.success('Dados salvos com sucesso!');
                    setVisualData({
                        ...visualData,
                        cantFinalize: true,
                        finalizing: false,
                        canErase: false,
                        counter: 0,
                    });
                    localDb.resetTable();
                    setMeasures({ type: 'reset' });
                } else {
                    measurePhotos.delete('inputs');
                    setVisualData({ ...visualData, cantFinalize: false, finalizing: false });
                }
            });
    };

    const cleanupPhotos = () => {
        measurePhotos.delete('photos');
        canFinish({ erase: false, counter: 0 });
    };

    return (
        <div id="afericao-page">
            <PageHeader />
            <Header as="h3" className="mt-4 ml-4">
                <Icon.Group size="big">
                    <Icon name="tasks" className="primary-color" />
                </Icon.Group>
                <Header.Content className="primary-color">
                    Aferição
                    <Header.Subheader className="secondary-color">
                        <strong>Análise e coleta de dados</strong>
                    </Header.Subheader>
                </Header.Content>
            </Header>

            <Divider fitted style={{ margin: '0 12px 10px 12px', borderTopWidth: '2px' }} />

            <div>
                {visualData.loading ? (
                    <Loader active={visualData.loading} content="Buscando informações" />
                ) : (
                    <>
                        <table className="content-table">
                            <thead>
                                <tr className="beak-row-header">
                                    <th>Bico</th>
                                    <th>Tanque</th>
                                    <th>Nº Série</th>
                                    <th>Produto</th>
                                    <th colSpan="2">Aferição</th>
                                    <th colSpan="2">Encerrante Mecânico</th>
                                    <th colSpan="2">Encerrante Eletrônico</th>
                                </tr>
                            </thead>

                            <tbody>
                                {bicos.map((bico) => {
                                    const measure = measures.measures.get(bico.serie);

                                    return (
                                        <tr key={bico.serie} className="beak-row">
                                            <td className="beak-item-small beak-number">
                                                {bico.bico}
                                            </td>
                                            <td className="beak-item-small">{bico.tanque}</td>
                                            <td className="beak-item-small">{bico.serie}</td>
                                            <td className="beak-item type">{bico.produto}</td>
                                            <td className="beak-item-small">
                                                <input
                                                    autoComplete="false"
                                                    onKeyDown={onKeyDownMeasure}
                                                    name="rapida"
                                                    type="text"
                                                    maxLength={3}
                                                    className="input-afericao"
                                                    placeholder="Rápida"
                                                    value={measure?.rapida || ''}
                                                    onChange={() => handleInput(bico.serie)}
                                                    onBlur={() => saveOnBlur(bico.serie)}
                                                />
                                            </td>
                                            <td className="beak-item-small">
                                                <input
                                                    autoComplete="false"
                                                    onKeyDown={onKeyDownMeasure}
                                                    maxLength={3}
                                                    name="lenta"
                                                    type="text"
                                                    className="input-afericao"
                                                    placeholder="Lenta"
                                                    value={measure?.lenta || ''}
                                                    onChange={() => handleInput(bico.serie)}
                                                    onBlur={() => saveOnBlur(bico.serie)}
                                                />
                                            </td>
                                            <td className="beak-item">
                                                <InputMask
                                                    autoComplete="false"
                                                    onKeyDown={onKeyDownMeasure}
                                                    className="inputEncerrante"
                                                    name="encerranteMecIni"
                                                    placeholder="Inicial"
                                                    value={measure?.encerranteMecIni || ''}
                                                    onChange={() => handleInput(bico.serie)}
                                                    onBlur={() => saveOnBlur(bico.serie)}
                                                />
                                            </td>
                                            <td className="beak-item">
                                                <InputMask
                                                    autoComplete="false"
                                                    onKeyDown={onKeyDownMeasure}
                                                    className="inputEncerrante"
                                                    name="encerranteMecFim"
                                                    placeholder="Final"
                                                    value={measure?.encerranteMecFim || ''}
                                                    onChange={() => handleInput(bico.serie)}
                                                    onBlur={() => saveOnBlur(bico.serie)}
                                                />
                                            </td>
                                            <td className="beak-item">
                                                <InputMask
                                                    autoComplete="false"
                                                    onKeyDown={onKeyDownMeasure}
                                                    className="inputEncerrante"
                                                    name="encerranteEletIni"
                                                    placeholder="Inicial"
                                                    value={measure?.encerranteEletIni || ''}
                                                    onChange={() => handleInput(bico.serie)}
                                                    onBlur={() => saveOnBlur(bico.serie)}
                                                />
                                            </td>
                                            <td className="beak-item">
                                                <InputMask
                                                    autoComplete="false"
                                                    onKeyDown={onKeyDownMeasure}
                                                    name="encerranteEletFim"
                                                    className="inputEncerrante"
                                                    placeholder="Final"
                                                    value={measure?.encerranteEletFim || ''}
                                                    onChange={() => handleInput(bico.serie)}
                                                    onBlur={() => saveOnBlur(bico.serie, bico.bico)}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        <div className="finalize-container">
                            <div>
                                <div className="input-img">
                                    <input
                                        type="file"
                                        id="cupom"
                                        multiple
                                        className="cupom-img"
                                        onChange={() => handleInputFiles()}
                                    />
                                    <label
                                        htmlFor="cupom"
                                        className="cupom-label"
                                        accept=".png, .jpg, .jpeg, .pdf"
                                    >
                                        <p>Inserir cupons</p>
                                        <Icon name="image" className="input-icon" size="large" />
                                    </label>
                                </div>
                                {visualData.canErase ? (
                                    <div className="imgs-info">
                                        <label className="input-counter">
                                            {visualData.counter} Itens
                                        </label>
                                        <button
                                            onClick={() => cleanupPhotos()}
                                            className="input-cleanup"
                                        >
                                            Limpar <Icon name="close" />
                                        </button>
                                    </div>
                                ) : (
                                    ''
                                )}
                            </div>

                            <Button
                                type="button"
                                value="Finalizar"
                                className="finalize-button"
                                disabled={visualData.cantFinalize}
                                onClick={() => sendData()}
                            >
                                <label>Finalizar</label>
                                <Icon name="angle double right" size="large" className="end-icon" />
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
